/**
 * Email Obfuscation Utilities
 *
 * Provides functions to obfuscate and deobfuscate email addresses to prevent
 * automated scraping while maintaining accessibility for legitimate users.
 *
 * Uses Base64 encoding combined with character reversal for simple but effective
 * obfuscation that is transparent to users but difficult for basic scrapers.
 */

// Browser environment check and polyfills for testing environments
const isNodeEnvironment = typeof window === 'undefined';

// Polyfill for Node.js testing environment
const base64Encode = isNodeEnvironment
	? (str) => Buffer.from(str, 'utf8').toString('base64')
	: (str) => btoa(str);

const base64Decode = isNodeEnvironment
	? (str) => Buffer.from(str, 'base64').toString('utf8')
	: (str) => atob(str);

/**
 * Obfuscates an email address using Base64 encoding and character reversal
 * @param {string} email - The email address to obfuscate
 * @returns {string} - The obfuscated email string
 * @throws {Error} - If email is invalid or empty
 */
export const obfuscateEmail = (email) => {
	// Input validation
	if (!email || typeof email !== 'string') {
		throw new Error('Email must be a non-empty string');
	}

	// Basic email format validation (simple check for @ symbol and basic structure)
	const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
	if (!emailRegex.test(email.trim())) {
		throw new Error('Invalid email format');
	}

	try {
		// Step 1: Reverse the email string
		const reversed = email.split('').reverse().join('');

		// Step 2: Base64 encode the reversed string
		const encoded = base64Encode(reversed);

		// Step 3: Add a prefix to identify our obfuscated emails
		return `obs_${encoded}`;
	} catch (error) {
		throw new Error(`Failed to obfuscate email: ${error.message}`);
	}
};

/**
 * Deobfuscates an email address that was obfuscated with obfuscateEmail
 * @param {string} obfuscatedEmail - The obfuscated email string
 * @returns {string} - The original email address
 * @throws {Error} - If obfuscated email is invalid or cannot be decoded
 */
export const deobfuscateEmail = (obfuscatedEmail) => {
	// Input validation
	if (!obfuscatedEmail || typeof obfuscatedEmail !== 'string') {
		throw new Error('Obfuscated email must be a non-empty string');
	}

	// Check for our obfuscation prefix
	if (!obfuscatedEmail.startsWith('obs_')) {
		throw new Error('Invalid obfuscated email format - missing prefix');
	}

	try {
		// Step 1: Remove the prefix
		const encoded = obfuscatedEmail.substring(4);

		// Step 2: Base64 decode
		const reversed = base64Decode(encoded);

		// Step 3: Reverse the string back to original
		const original = reversed.split('').reverse().join('');

		// Validate the result is a proper email
		const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
		if (!emailRegex.test(original)) {
			throw new Error('Deobfuscated result is not a valid email');
		}

		return original;
	} catch (error) {
		throw new Error(`Failed to deobfuscate email: ${error.message}`);
	}
};

/**
 * Checks if a string is an obfuscated email
 * @param {string} str - The string to check
 * @returns {boolean} - True if the string appears to be an obfuscated email
 */
export const isObfuscatedEmail = (str) => {
	return typeof str === 'string' && str.startsWith('obs_');
};

/**
 * Safely obfuscates an email, returning the original if obfuscation fails
 * @param {string} email - The email address to obfuscate
 * @returns {string} - The obfuscated email or original email if obfuscation fails
 */
export const safeObfuscateEmail = (email) => {
	try {
		return obfuscateEmail(email);
	} catch (error) {
		// Log the error in development/testing environments
		if (process.env.NODE_ENV !== 'production') {
			// eslint-disable-next-line no-console
			console.warn('Email obfuscation failed:', error.message);
		}
		return email;
	}
};

/**
 * Safely deobfuscates an email, returning the original if deobfuscation fails
 * @param {string} obfuscatedEmail - The obfuscated email string
 * @returns {string} - The deobfuscated email or original string if deobfuscation fails
 */
export const safeDeobfuscateEmail = (obfuscatedEmail) => {
	try {
		return deobfuscateEmail(obfuscatedEmail);
	} catch (error) {
		// Log the error in development/testing environments
		if (process.env.NODE_ENV !== 'production') {
			// eslint-disable-next-line no-console
			console.warn('Email deobfuscation failed:', error.message);
		}
		return obfuscatedEmail;
	}
};
